import { useState, useEffect, useRef } from "react";
import type { Field, DocumentIntelligence, Theme, FontPairing, FieldGroup } from "@structurify/shared-types";
import { MonoLabel, Rule, KeyHint, TypeGlyph, ConfidenceDot, QuietButton } from "../components/primitives/index.ts";
import { InsightsPanel } from "../intelligence/InsightsPanel.tsx";
import { FieldChips } from "../intelligence/FieldChips.tsx";
import { ClaimDocument } from "../document/ClaimDocument.tsx";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  fields: Field[];
  layout: "split" | "drawer" | "focused";
  onExport: () => void;
  intel: DocumentIntelligence | null;
  focusFieldId: string | null;
  fileName?: string | undefined;
  objectUrl?: string | null | undefined;
  mimeType?: string | undefined;
}

function useNarrow() {
  const [narrow, setNarrow] = useState(window.innerWidth < 880);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 880);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return narrow;
}

const Y_MAP: Record<string, number> = {
  claim_number: 78, date_of_loss: 106, policy_number: 134,
  insured_name: 198, insured_dob: 232, insured_phone: 232,
  insured_address: 266, loss_location: 350, loss_type: 384,
  loss_description: 422, estimated_amount: 524, deductible: 524,
  adjuster_name: 588, signature_date: 712,
};

export function StageReview({ theme, fonts, fields, layout, onExport, intel, focusFieldId, fileName, objectUrl, mimeType }: Props) {
  const [activeId, setActiveId] = useState(focusFieldId ?? (fields[0]?.id ?? ""));
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.id, f.value]))
  );
  const [verified, setVerified] = useState<Set<string>>(new Set());
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [insightsOpen, setInsightsOpen] = useState(true);
  const [mobilePane, setMobilePane] = useState<"doc" | "data" | "insights">("data");
  const docScrollRef = useRef<HTMLDivElement>(null);
  const isNarrow = useNarrow();

  useEffect(() => { if (focusFieldId) setActiveId(focusFieldId); }, [focusFieldId]);

  const toggleVerify = (id: string) => {
    setVerified((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };
  const toggleFlag = (id: string) => {
    setFlagged((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (editingId) return;
      const idx = fields.findIndex((f) => f.id === activeId);
      if (e.key === "j" || e.key === "ArrowDown") { e.preventDefault(); setActiveId(fields[Math.min(idx + 1, fields.length - 1)]?.id ?? activeId); }
      else if (e.key === "k" || e.key === "ArrowUp") { e.preventDefault(); setActiveId(fields[Math.max(idx - 1, 0)]?.id ?? activeId); }
      else if (e.key === "v") toggleVerify(activeId);
      else if (e.key === "f") toggleFlag(activeId);
      else if (e.key === "Enter") setEditingId(activeId);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeId, editingId, fields]);

  useEffect(() => {
    const el = docScrollRef.current;
    if (!el) return;
    const y = Y_MAP[activeId] ?? 0;
    el.scrollTo({ top: Math.max(0, y - 120), behavior: "smooth" });
  }, [activeId]);

  const active = fields.find((f) => f.id === activeId) ?? null;
  const verifiedCount = verified.size;
  const total = fields.length;
  const allVerified = verifiedCount === total;

  const isSplit = layout === "split";
  const isDrawer = layout === "drawer";
  const isFocused = layout === "focused";
  const showDoc = !isFocused && (!isNarrow || mobilePane === "doc");
  const showData = !isNarrow || mobilePane === "data";
  const showInsights = intel != null && insightsOpen && (!isNarrow || mobilePane === "insights");

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderBottom: `1px solid ${theme.rule}`, gap: 12, flexShrink: 0, background: theme.surface, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <MonoLabel theme={theme} fonts={fonts} size={9}>DOC</MonoLabel>
          <span style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {fileName ?? "document"}
          </span>
        </div>
        {!isNarrow && (
          <>
            <Rule theme={theme} vertical style={{ height: 18 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <MonoLabel theme={theme} fonts={fonts} size={9}>EXTRACTED</MonoLabel>
              <span style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, fontWeight: 500 }}>{fields.length} values</span>
            </div>
          </>
        )}

        <div style={{ flex: 1 }} />

        {isNarrow && (
          <div style={{ display: "flex", border: `1px solid ${theme.rule}`, order: 99, width: "100%", marginTop: 6 }}>
            {(["doc", "data", "insights"] as const).map((k, i) => (
              <button key={k} onClick={() => setMobilePane(k)} style={{ flex: 1, padding: "7px 8px", fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", background: mobilePane === k ? theme.ink : "transparent", color: mobilePane === k ? theme.docPaper : theme.inkSoft, border: "none", borderLeft: i > 0 ? `1px solid ${theme.rule}` : "none", cursor: "pointer" }}>
                {k.charAt(0).toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>
        )}

        {!isNarrow && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MonoLabel theme={theme} fonts={fonts} size={9}>VERIFIED</MonoLabel>
            <div style={{ display: "flex", gap: 2 }}>
              {fields.map((f) => (
                <div key={f.id} style={{ width: 8, height: 14, background: verified.has(f.id) ? theme.ink : flagged.has(f.id) ? theme.confidenceLow : theme.rule, opacity: flagged.has(f.id) ? 0.6 : 1, transition: "all 200ms" }} />
              ))}
            </div>
            <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkSoft }}>
              {String(verifiedCount).padStart(2, "0")}/{total}
            </span>
          </div>
        )}

        {!isNarrow && <Rule theme={theme} vertical style={{ height: 18 }} />}

        {!isNarrow && intel && (
          <button
            onClick={() => setInsightsOpen((o) => !o)}
            style={{ fontFamily: fonts.sans, fontSize: 12, fontWeight: 500, padding: "6px 11px", background: insightsOpen ? theme.ink : "transparent", color: insightsOpen ? theme.docPaper : theme.ink, border: `1px solid ${insightsOpen ? theme.ink : theme.rule}`, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.06em" }}>{insightsOpen ? "▸" : "◂"}</span>
            Intelligence
            <span style={{ fontFamily: fonts.mono, fontSize: 9, padding: "1px 5px", background: insightsOpen ? "rgba(255,255,255,0.18)" : theme.chipBg, color: insightsOpen ? theme.docPaper : theme.inkSoft }}>
              {intel.findings.length}
            </span>
          </button>
        )}

        <QuietButton theme={theme} fonts={fonts} primary={allVerified} onClick={onExport} style={{ padding: "7px 14px", fontSize: 12 }}>
          {allVerified ? "Export — ready" : "Export"}
          <svg width="9" height="9" viewBox="0 0 9 9"><path d="M2 4.5 L7 4.5 M5 2 L7 4.5 L5 7" stroke="currentColor" strokeWidth="1" fill="none" /></svg>
        </QuietButton>
      </div>

      {/* Main body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {showDoc && (
          <div
            ref={docScrollRef}
            style={{ flex: isSplit ? 1.1 : 1.4, overflow: "auto", padding: "clamp(16px, 3vw, 32px)", background: theme.bg, minWidth: 0, borderRight: isDrawer ? "none" : `1px solid ${theme.rule}` }}
          >
            <div style={{ width: 612, maxWidth: "100%", margin: "0 auto", position: "relative" }}>
              {objectUrl ? (
                mimeType?.startsWith("image/") ? (
                  <img src={objectUrl} alt="Document" style={{ width: "100%", height: "auto", display: "block", border: `1px solid ${theme.rule}` }} />
                ) : (
                  <iframe src={objectUrl} title="Document" style={{ width: "100%", height: "900px", border: `1px solid ${theme.rule}`, display: "block" }} />
                )
              ) : (
                <>
                  <ClaimDocument activeField={activeId} hoveredField={hoverId} theme={theme} />
                  {!isNarrow && <EvidenceCallout activeId={activeId} theme={theme} fonts={fonts} field={active} />}
                </>
              )}
            </div>
          </div>
        )}

        {showData && (
          <div
            style={{
              flex: isFocused ? 1 : isSplit ? 0.9 : 0.7,
              maxWidth: isFocused ? "none" : isNarrow ? "none" : 540,
              overflow: "auto",
              background: theme.surface,
              minWidth: 0,
              position: isDrawer ? "absolute" : "relative",
              right: isDrawer ? 0 : "auto",
              top: isDrawer ? 0 : "auto",
              bottom: isDrawer ? 0 : "auto",
              width: isDrawer ? 480 : "auto",
              boxShadow: isDrawer ? "-20px 0 40px rgba(20,16,10,0.06)" : "none",
              borderLeft: isDrawer ? `1px solid ${theme.rule}` : "none",
            }}
          >
            <DataList
              fields={fields} values={values} setValues={setValues}
              activeId={activeId} setActiveId={setActiveId}
              hoverId={hoverId} setHoverId={setHoverId}
              editingId={editingId} setEditingId={setEditingId}
              verified={verified} toggleVerify={toggleVerify}
              flagged={flagged} toggleFlag={toggleFlag}
              theme={theme} fonts={fonts} intel={intel}
            />
          </div>
        )}

        {showInsights && intel && (
          <InsightsPanel
            theme={theme} fonts={fonts} intel={intel} fields={fields}
            onJumpToField={(id) => { setActiveId(id); if (isNarrow) setMobilePane("data"); }}
            onClose={() => (isNarrow ? setMobilePane("data") : setInsightsOpen(false))}
            mobile={isNarrow}
          />
        )}
      </div>

      {!isNarrow && (
        <div style={{ flexShrink: 0, padding: "10px 28px", borderTop: `1px solid ${theme.rule}`, background: theme.surface, display: "flex", alignItems: "center", gap: 22, fontFamily: fonts.sans, fontSize: 11.5, color: theme.inkMute }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <KeyHint theme={theme} fonts={fonts}>J</KeyHint>
            <KeyHint theme={theme} fonts={fonts}>K</KeyHint>
            next / previous
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <KeyHint theme={theme} fonts={fonts}>V</KeyHint> verify
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <KeyHint theme={theme} fonts={fonts}>F</KeyHint> flag
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <KeyHint theme={theme} fonts={fonts}>↵</KeyHint> edit value
          </span>
          <div style={{ flex: 1 }} />
          <span>{flagged.size > 0 ? `${flagged.size} flagged for follow-up` : "No issues flagged"}</span>
        </div>
      )}
    </div>
  );
}

const GROUPS: FieldGroup[] = ["Header", "Insured", "Loss", "Amounts", "Adjuster", "Signature"];

function DataList({
  fields, values, setValues, activeId, setActiveId, hoverId, setHoverId,
  editingId, setEditingId, verified, toggleVerify, flagged, toggleFlag, theme, fonts, intel,
}: {
  fields: Field[]; values: Record<string, string>; setValues: (v: Record<string, string>) => void;
  activeId: string; setActiveId: (id: string) => void;
  hoverId: string | null; setHoverId: (id: string | null) => void;
  editingId: string | null; setEditingId: (id: string | null) => void;
  verified: Set<string>; toggleVerify: (id: string) => void;
  flagged: Set<string>; toggleFlag: (id: string) => void;
  theme: Theme; fonts: FontPairing; intel: DocumentIntelligence | null;
}) {
  return (
    <div style={{ padding: "20px 0" }}>
      {GROUPS.map((g) => {
        const gFields = fields.filter((f) => f.group === g);
        if (!gFields.length) return null;
        return (
          <div key={g} style={{ marginBottom: 14 }}>
            <div style={{ padding: "6px 24px 4px" }}>
              <MonoLabel theme={theme} fonts={fonts} size={9}>§ {g}</MonoLabel>
            </div>
            {gFields.map((f) => (
              <DataRow
                key={f.id} field={f}
                value={values[f.id] ?? f.value}
                setValue={(v) => setValues({ ...values, [f.id]: v })}
                active={activeId === f.id}
                onActivate={() => setActiveId(f.id)}
                onHover={(h) => setHoverId(h ? f.id : null)}
                editing={editingId === f.id}
                setEditing={(e) => setEditingId(e ? f.id : null)}
                verified={verified.has(f.id)} toggleVerify={() => toggleVerify(f.id)}
                flagged={flagged.has(f.id)} toggleFlag={() => toggleFlag(f.id)}
                theme={theme} fonts={fonts} intel={intel}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DataRow({
  field, value, setValue, active, onActivate, onHover, editing, setEditing,
  verified, toggleVerify, flagged, toggleFlag, theme, fonts, intel,
}: {
  field: Field; value: string; setValue: (v: string) => void;
  active: boolean; onActivate: () => void; onHover: (h: boolean) => void;
  editing: boolean; setEditing: (e: boolean) => void;
  verified: boolean; toggleVerify: () => void;
  flagged: boolean; toggleFlag: () => void;
  theme: Theme; fonts: FontPairing; intel: DocumentIntelligence | null;
}) {
  const low = field.confidence < 0.8;
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  return (
    <div
      onClick={onActivate}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{ padding: "12px 24px", borderLeft: `2px solid ${active ? theme.ink : "transparent"}`, background: active ? theme.panel : "transparent", cursor: "pointer", position: "relative", transition: "background 160ms, border-color 160ms" }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <TypeGlyph type={field.type} theme={theme} size={10} />
        <span style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkSoft, fontWeight: 500, letterSpacing: "-0.005em", flex: 1 }}>{field.label}</span>
        {low && <span style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.confidenceLow, letterSpacing: "0.06em", textTransform: "uppercase" }}>uncertain</span>}
        <ConfidenceDot value={field.confidence} theme={theme} size={5} />
      </div>

      {editing ? (
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditing(false); }}
          style={{ width: "100%", fontFamily: fonts.serif, fontSize: 16, background: "transparent", border: "none", borderBottom: `1px solid ${theme.ink}`, color: theme.ink, outline: "none", padding: "2px 0" }}
        />
      ) : (
        <div
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          style={{ fontFamily: field.type === "currency" || field.type === "phone" || field.type === "date" ? fonts.mono : fonts.serif, fontSize: field.type === "long_text" ? 13 : 16, lineHeight: 1.35, color: theme.ink, fontWeight: 400 }}
        >
          {value}
        </div>
      )}

      <FieldChips fieldId={field.id} intel={intel} theme={theme} fonts={fonts} />

      {active && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 10, opacity: 0, animation: "fadeIn 200ms forwards" }}>
          <RowButton theme={theme} fonts={fonts} active={verified} onClick={(e) => { e.stopPropagation(); toggleVerify(); }}>
            {verified ? "✓ Verified" : "Verify"}
          </RowButton>
          <RowButton theme={theme} fonts={fonts} onClick={(e) => { e.stopPropagation(); setEditing(true); }}>Edit</RowButton>
          <RowButton theme={theme} fonts={fonts} active={flagged} danger onClick={(e) => { e.stopPropagation(); toggleFlag(); }}>
            {flagged ? "⚑ Flagged" : "Flag"}
          </RowButton>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.05em" }}>
            p.{field.page} · {Math.round(field.confidence * 100)}% conf
          </span>
        </div>
      )}
    </div>
  );
}

function RowButton({
  children, onClick, active, danger, theme, fonts,
}: {
  children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  active?: boolean; danger?: boolean; theme: Theme; fonts: FontPairing;
}) {
  return (
    <button
      onClick={onClick}
      style={{ fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 450, background: active ? (danger ? theme.confidenceLow : theme.ink) : "transparent", color: active ? theme.docPaper : theme.inkSoft, border: `1px solid ${active ? "transparent" : theme.rule}`, padding: "4px 10px", borderRadius: 2, cursor: "pointer", transition: "all 160ms" }}
    >
      {children}
    </button>
  );
}

const EVIDENCE_POSITIONS: Record<string, { top: number; side: "left" | "right" }> = {
  claim_number:    { top: 78,  side: "right" },
  date_of_loss:    { top: 106, side: "right" },
  policy_number:   { top: 134, side: "right" },
  insured_name:    { top: 198, side: "left" },
  insured_dob:     { top: 232, side: "left" },
  insured_phone:   { top: 232, side: "right" },
  insured_address: { top: 266, side: "left" },
  loss_location:   { top: 350, side: "left" },
  loss_type:       { top: 384, side: "left" },
  loss_description:{ top: 450, side: "left" },
  estimated_amount:{ top: 524, side: "left" },
  deductible:      { top: 524, side: "right" },
  adjuster_name:   { top: 588, side: "left" },
  signature_date:  { top: 712, side: "right" },
};

function EvidenceCallout({ activeId, theme, fonts, field }: { activeId: string; theme: Theme; fonts: FontPairing; field: Field | null }) {
  const pos = EVIDENCE_POSITIONS[activeId];
  if (!pos || !field) return null;
  const isRight = pos.side === "right";
  return (
    <div
      key={activeId}
      style={{ position: "absolute", top: pos.top - 8, [isRight ? "left" : "right"]: 612 + 12, animation: "calloutIn 320ms cubic-bezier(.2,.7,.2,1) forwards", opacity: 0, pointerEvents: "none" }}
    >
      <div style={{ background: theme.ink, color: theme.docPaper, fontFamily: fonts.mono, fontSize: 10, padding: "6px 10px", whiteSpace: "nowrap", letterSpacing: "0.04em", position: "relative" }}>
        SOURCE · PAGE {field.page} · {Math.round(field.confidence * 100)}%
        <div
          style={{
            position: "absolute", top: "50%", transform: "translateY(-50%)",
            [isRight ? "right" : "left"]: -6,
            width: 0, height: 0,
            borderTop: "6px solid transparent", borderBottom: "6px solid transparent",
            [isRight ? "borderLeft" : "borderRight"]: `6px solid ${theme.ink}`,
          }}
        />
      </div>
    </div>
  );
}
